import { Box, Stack, StackProps, Typography, useTheme } from '@mui/material';
import {
  Control,
  FieldPath,
  FieldValues,
  useFormContext,
} from 'react-hook-form';

import { RadioOption, RadioToggle } from '@/components/Form/RadioToggle';

import { LISTED_CATEGORIES_QUESTION } from '@/constants/document';

import { useTranslate } from '@/hooks/useTranslate';

import { EmploymentStatus } from '@/types/user';

interface EmploymentRiskSectorQuestionProps<T extends FieldValues> {
  control: Control<T>;
  name: FieldPath<T>;
  options: RadioOption[];
  employmentStatus?: EmploymentStatus;
  sx?: StackProps['sx'];
}

const EmploymentRiskSectorQuestion = <T extends FieldValues>({
  control,
  name,
  options,
  employmentStatus = EmploymentStatus.Employed,
  sx,
}: EmploymentRiskSectorQuestionProps<T>) => {
  const { t } = useTranslate();
  const theme = useTheme();
  const { watch } = useFormContext();
  const riskSectorFlag = watch('employmentDetailed.riskSectorFlag');

  return (
    <Stack
      spacing={1}
      mb={2}
      direction={'column'}
      alignItems={'flex-start'}
      bgcolor={theme.palette.grey[100]}
      sx={{ padding: 1.5, borderRadius: 1.25, ...sx }}
    >
      <Typography variant="subtitle1" className="question-title">
        {t(
          `employmentForm.form.employmentRiskSector.${employmentStatus}.question.label`,
        )}
      </Typography>
      <Typography variant="body2" mb={2} className="question-content">
        {t(LISTED_CATEGORIES_QUESTION.description)}
      </Typography>
      <Box
        width="100%"
        bgcolor={theme.palette.background.paper}
        padding={1.5}
        borderRadius={1.25}
        className="question-options-container"
      >
        <RadioToggle<T>
          control={control}
          name={name}
          options={options}
          rules={{
            required:
              typeof riskSectorFlag === 'boolean'
                ? undefined
                : t('questionDrawer.accountPurpose.accountType.required'),
          }}
          sx={{
            '& .MuiFormControlLabel-root': {
              flex: 1,
            },
          }}
        />
      </Box>
    </Stack>
  );
};

export default EmploymentRiskSectorQuestion;
